import { useState } from "react";
import classNames from "classnames";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
} from "@/components";
import { API_URL } from "@/constants/env.const";
import type { ApiAsset } from "../../_types/assets.types";
import {
  DotsVerticalIcon,
  File02Icon,
  Image01Icon,
  VideoRecorderIcon,
  VolumeMaxIcon,
} from "@vetty/icons";
import styles from "./AssetListItem.module.scss";

type AssetListItemProps = {
  asset: ApiAsset;
  formatBytes: (n: number) => string;
  onDuplicate: () => void;
  onDelete: () => void;
  actionsDisabled: boolean;
};

const KindIcon = ({ kind }: { kind: string }) => {
  const s = { width: 20, height: 20 };
  switch (kind) {
    case "image":
      return <Image01Icon style={s} className={styles["comp__thumbIcon"]} />;
    case "audio":
      return <VolumeMaxIcon style={s} className={styles["comp__thumbIcon"]} />;
    case "video":
      return <VideoRecorderIcon style={s} className={styles["comp__thumbIcon"]} />;
    default:
      return <File02Icon style={s} className={styles["comp__thumbIcon"]} />;
  }
};

export const AssetListItem = ({
  asset,
  formatBytes,
  onDuplicate,
  onDelete,
  actionsDisabled,
}: AssetListItemProps) => {
  const [thumbFailed, setThumbFailed] = useState(false);
  const showImageThumb = asset.kind === "image" && !thumbFailed;
  const fileUrl = `${API_URL.replace(/\/$/, "")}/v2/assets/${asset.id}/file`;

  return (
    <div className={styles["comp"]}>
      <div className={styles["comp__nameCell"]}>
        <div className={styles["comp__nameInner"]}>
          <div className={styles["comp__thumb"]}>
            {showImageThumb ? (
              <img
                src={fileUrl}
                alt=""
                loading="lazy"
                onError={() => setThumbFailed(true)}
              />
            ) : (
              <KindIcon kind={asset.kind} />
            )}
          </div>
          <span className={styles["comp__name"]}>{asset.name}</span>
        </div>
      </div>

      <div className={styles["comp__kind"]}>
        <span className={styles["comp__badge"]}>{asset.kind}</span>
      </div>

      <div className={styles["comp__mime"]}>{asset.mimeType}</div>

      <div className={styles["comp__size"]}>{formatBytes(asset.sizeBytes)}</div>

      <div className={styles["comp__actions"]}>
        <Dropdown>
          <DropdownTrigger className={styles["comp__dropdownTrigger"]}>
            <button
              type="button"
              className={styles["comp__menuTrigger"]}
              aria-label="Asset actions"
              aria-haspopup="menu"
              disabled={actionsDisabled}
            >
              <DotsVerticalIcon style={{ width: 20, height: 20 }} aria-hidden />
            </button>
          </DropdownTrigger>
          <DropdownContent width={180} lockBodyScroll={false}>
            <DropdownItem
              id={`${asset.id}-duplicate`}
              selectable={false}
              className={styles["comp__menuItem"]}
              disabled={actionsDisabled}
              onClick={() => onDuplicate()}
            >
              Duplicate
            </DropdownItem>
            <DropdownItem
              id={`${asset.id}-delete`}
              selectable={false}
              className={classNames(styles["comp__menuItem"], styles["comp__menuItem--danger"])}
              disabled={actionsDisabled}
              onClick={() => onDelete()}
            >
              Delete
            </DropdownItem>
          </DropdownContent>
        </Dropdown>
      </div>
    </div>
  );
};
